import { ColumnDef } from '@tanstack/react-table'
import { Honoraire } from '@/api/honoraires'
import { DataTableColumnHeader } from '@/components/data-table-column-header'
import { Badge } from '@/components/ui/badge'
import { Link } from '@tanstack/react-router'
import i18n from '@/i18n'
import { AlertTriangle } from 'lucide-react'

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('fr-MA', { style: 'currency', currency: 'MAD' }).format(amount)

const getStatusLabel = (s: string) => {
  if (s === 'paid') return i18n.t('honoraires_status_paid')
  if (s === 'partial') return i18n.t('honoraires_status_partial')
  if (s === 'unpaid') return i18n.t('honoraires_status_unpaid')
  return i18n.t('honoraires_status_pending')
}

const getStatusClass = (s: string) => {
  if (s === 'paid') return 'bg-green-100 text-green-800 border-green-200'
  if (s === 'partial') return 'bg-yellow-100 text-yellow-800 border-yellow-200'
  if (s === 'unpaid') return 'bg-red-100 text-red-800 border-red-200'
  return 'bg-gray-100 text-gray-800 border-gray-200'
}

export const columns: ColumnDef<Honoraire>[] = [
  {
    accessorKey: 'case_number',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('honoraires_col_case_number')} />
    ),
    cell: ({ row }) => (
      // @ts-ignore
      <Link to={`/cases/${row.original.internal_id}`} className="font-medium text-blue-600 hover:underline">
        {row.original.case_number}
      </Link>
    ),
    enableSorting: false,
  },
  {
    accessorKey: 'client_name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('honoraires_col_client')} />
    ),
    cell: ({ row }) => {
      const { client_id, client_name } = row.original
      if (client_id) {
        return (
          // @ts-ignore
          <Link to={`/clients/${client_id}`} className="hover:underline">
            {client_name}
          </Link>
        )
      }
      return <span>{client_name}</span>
    },
    enableSorting: false,
  },
  {
    accessorKey: 'tribunal_name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('honoraires_col_tribunal')} />
    ),
    enableSorting: false,
  },
  {
    accessorKey: 'total_fee',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('honoraires_col_total_fee')} />
    ),
    cell: ({ row }) => (
      <span className="font-bold">{formatCurrency(row.original.total_fee)}</span>
    ),
    enableSorting: false,
  },
  {
    accessorKey: 'total_paid',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('honoraires_col_amount_paid')} />
    ),
    cell: ({ row }) => {
      const { total_paid, deposits_received, formally_paid } = row.original
      const hasBreakdown = (deposits_received ?? 0) > 0 || (formally_paid ?? 0) > 0
      return (
        <div>
          <span className="font-semibold text-green-600">{formatCurrency(total_paid)}</span>
          {hasBreakdown && (
            <div className="flex gap-2 text-xs text-muted-foreground mt-0.5">
              {(deposits_received ?? 0) > 0 && (
                <span title={i18n.t('honoraires_deposits_received')}>
                  {i18n.t('honoraires_prov_abbr')}: {formatCurrency(deposits_received)}
                </span>
              )}
              {(formally_paid ?? 0) > 0 && (
                <span title={i18n.t('honoraires_formally_paid')}>
                  {i18n.t('honoraires_formal_abbr')}: {formatCurrency(formally_paid)}
                </span>
              )}
            </div>
          )}
        </div>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: 'remaining',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('honoraires_col_amount_remaining')} />
    ),
    cell: ({ row }) => {
      const { remaining, total_invoiced, total_fee } = row.original
      const inv = total_invoiced ?? 0
      const gap = total_fee - inv
      const hasGap   = gap > 0.01
      const overInv  = gap < -0.01
      return (
        <div>
          <span className="font-bold text-red-600">{formatCurrency(remaining)}</span>
          {inv > 0 && (
            <div className="text-xs text-muted-foreground mt-0.5" title={i18n.t('honoraires_total_invoiced')}>
              {i18n.t('honoraires_invoiced_abbr')}: {formatCurrency(inv)} HT
            </div>
          )}
          {hasGap && (
            <div className="flex items-center gap-0.5 text-xs text-amber-600 mt-0.5">
              <AlertTriangle className="h-3 w-3" />
              <span>{i18n.t('honoraires_gap_warning', { amount: formatCurrency(gap) })}</span>
            </div>
          )}
          {overInv && (
            <div className="flex items-center gap-0.5 text-xs text-orange-600 mt-0.5">
              <AlertTriangle className="h-3 w-3" />
              <span>{i18n.t('honoraires_credit_note_warning')}</span>
            </div>
          )}
        </div>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: 'status',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('honoraires_col_status')} />
    ),
    cell: ({ row }) => (
      <Badge variant="outline" className={getStatusClass(row.original.status)}>
        {getStatusLabel(row.original.status)}
      </Badge>
    ),
    enableSorting: false,
  },
]
